import { FC, useState, useEffect, useRef } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';

type TimerProps = {
  attemptStartedAt: string;
  timeAllowedMinutes: number;
  onTimeUp?: () => void;
};

const Timer: FC<TimerProps> = ({ attemptStartedAt, timeAllowedMinutes, onTimeUp }) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timeUpCalledRef = useRef(false);
  const attemptStartedAtRef = useRef(attemptStartedAt);
  const timeAllowedMinutesRef = useRef(timeAllowedMinutes);
  const onTimeUpRef = useRef(onTimeUp);

  // Keep refs updated
  attemptStartedAtRef.current = attemptStartedAt;
  timeAllowedMinutesRef.current = timeAllowedMinutes;
  onTimeUpRef.current = onTimeUp;

  // Calculate and update time remaining - only run once on mount
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const startedAt = new Date(attemptStartedAtRef.current).getTime();
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startedAt) / 1000);
      const totalSeconds = timeAllowedMinutesRef.current * 60;
      const remaining = Math.max(0, totalSeconds - elapsedSeconds);
      setTimeRemaining(remaining);

      // Call onTimeUp when time reaches 0
      if (remaining === 0 && !timeUpCalledRef.current && onTimeUpRef.current) {
        timeUpCalledRef.current = true;
        onTimeUpRef.current();
      }
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, []); // Empty dependency array - only run once

  // Format time remaining for display
  const formatTimeRemaining = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  // Determine if time is running low (less than 10 minutes)
  const isLowTime = timeRemaining !== null && timeRemaining < 600;

  if (timeRemaining === null) return null;

  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 ${
        isLowTime ? 'bg-red-50 border-red-400 text-red-700' : 'bg-yellow-50 border-yellow-400 text-yellow-800'
      }`}
    >
      <ClockIcon className="w-5 h-5" />
      <span className="font-semibold text-sm">Time remaining: {formatTimeRemaining(timeRemaining)}</span>
    </div>
  );
};

export default Timer;
